/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import React from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Tab, Tabs, Select, MenuItem } from "@mui/material";
import { sortBy } from "lodash";
import { Column } from "@material-table/core";
import { CreatePropertyFeatureFlag,
  CreatePropertyFeatureFlagVariables,
  FeatureFlagDashboard,
  FeatureFlagDashboard_propertyFeatureFlags as PropertyFeatureFlagType,
  FeatureFlagDashboard_featureFlags as FeatureFlagType,
  DeletePropertyFeatureFlag,
  DeletePropertyFeatureFlagVariables,
  UpdatePropertyFeatureFlag,
  UpdatePropertyFeatureFlagVariables } from "types/gql-types";
import { EditableTable } from "components/haven-design/editable-table";
import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";
import { LoadingSpinner } from "components/graphql-views";
import {
  query,
  updateFeatureFlag,
  deleteFeatureFlag,
  createPropertyFeatureFlag,
  updatePropertyFeatureFlag,
  deletePropertyFeatureFlag,
} from "./queries";
import { featureFlagLookup, propertyLookup } from "./utils";
import styles from "./styles.module.css";

enum TabsIndex {
  Global = 0,
  Property = 1,
}

const exportGlobalToCSV = (featureFlags?: FeatureFlagType[]) => {
  if (!featureFlags) return;

  const csvContent = [
    ["Feature", "Active", "Enabled by Default"],
    ...featureFlags.map(flag => [
      flag.name,
      flag.globalEnabled ? "Yes" : "No",
      flag.globalDefaultEnabled ? "Yes" : "No",
    ]),
  ].map(row => row.join(",")).join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", "global-feature-flags-export.csv");
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const exportPropertyToCSV = (
  propertyFeatureFlags?: PropertyFeatureFlagType[],
  properties?: any[],
  featureFlags?: FeatureFlagType[],
) => {
  if (!propertyFeatureFlags || !properties || !featureFlags) return;

  const propertyMap = properties.reduce((acc, prop) =>
    ({ ...acc, [prop.id]: prop.name }), {} as Record<string, string>);
  const featureMap = featureFlags.reduce((acc, flag) =>
    ({ ...acc, [flag.id]: flag.name }), {} as Record<string, string>);

  const csvContent = [
    ["Property", "Feature", "Active"],
    ...propertyFeatureFlags.map(flag => [
      propertyMap[flag.propertyId] || "Unknown",
      featureMap[flag.featureId] || "Unknown",
      flag.enabled ? "Yes" : "No",
    ]),
  ].map(row => row.join(",")).join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", "property-feature-flags-export.csv");
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const FeatureFlagsPage: React.FC = () => {
  const mutationArgs = { refetchQueries: ["FeatureFlagDashboard"] };
  const { openSnackbar } = useSnackbar();

  const [tabIndex, setTabIndex] = React.useState<TabsIndex>(0);
  const { data, loading, error } = useQuery<FeatureFlagDashboard>(query);

  const [updateFeature] = useMutation(updateFeatureFlag, mutationArgs);
  const [deleteFeature] = useMutation(deleteFeatureFlag, mutationArgs);
  const [createPropertyFeature] =
  useMutation<CreatePropertyFeatureFlag, CreatePropertyFeatureFlagVariables>(
    createPropertyFeatureFlag,
    mutationArgs,
  );
  const [updatePropertyFeature] =
  useMutation<UpdatePropertyFeatureFlag, UpdatePropertyFeatureFlagVariables>(
    updatePropertyFeatureFlag,
    mutationArgs,
  );
  const [deletePropertyFeature] =
  useMutation<DeletePropertyFeatureFlag, DeletePropertyFeatureFlagVariables>(
    deletePropertyFeatureFlag,
    mutationArgs,
  );
  if (loading) return <LoadingSpinner />;

  const featureFlags = sortBy(data?.featureFlags?.map((x) => (
    { ...x })) || [], [(featureFlag) => featureFlag.name]);
  const propertyFeatureFlags = data?.propertyFeatureFlags?.map((x) => ({ ...x })) || [];

  const properties = sortBy(data?.properties || [], [(property) => property.name]);
  const propertyColumns: Array<Column<PropertyFeatureFlagType>> = [
    {
      title: "Property",
      field: "propertyId",
      editable: "onAdd",
      lookup: propertyLookup(properties),
      defaultSort: "asc",
      editComponent: (cellProps) => (
        <Select
          data-cy="ff-row-property"
          value={cellProps.value || ""}
          onChange={(e) => cellProps.onChange?.((e.target as HTMLInputElement).value)}
          displayEmpty
        >
          <MenuItem value="" disabled>
            Select Property
          </MenuItem>
          {properties.map((prop) => (
            <MenuItem key={prop.id} value={prop.id}>{prop.name}</MenuItem>
          ))}
        </Select>
      ),
    },
    {
      title: "Feature",
      field: "featureId",
      editable: "onAdd",
      lookup: featureFlagLookup(featureFlags),
      editComponent: (cellProps) => (
        <Select
          data-cy="ff-row-feature"
          value={cellProps.value || ""}
          onChange={(e) => cellProps.onChange?.((e.target as HTMLInputElement).value)}
          displayEmpty
        >
          <MenuItem value="" disabled>
            Select Feature
          </MenuItem>
          {featureFlags.map((flag) => (
            <MenuItem key={flag.id} value={flag.id}>{flag.name}</MenuItem>
          ))}
        </Select>
      ),
    },
    { title: "Active", field: "enabled", type: "boolean", initialEditValue: true },
  ];

  if (error) {
    logger.sendError(error);
    return <div>Error loading feature flags</div>;
  }

  return (
    <div className={styles.container}>
      <h3>Feature Flags</h3>

      <Tabs value={tabIndex} onChange={(_, index) => setTabIndex(index)}>
        <Tab data-cy="ff-tab-global" label="Global" />
        <Tab data-cy="ff-tab-property" label="Property" />
      </Tabs>

      {tabIndex === TabsIndex.Global && (
        <EditableTable
          title="Global"
          data={featureFlags}
          isLoading={loading}
          options={{ pageSize: 10 }}
          columns={[
            { title: "Feature", field: "name", editable: "never", defaultSort: "asc" },
            { title: "Active", field: "globalEnabled", type: "boolean" },
            { title: "Enabled by Default", field: "globalDefaultEnabled", type: "boolean" },
          ]}
          actions={[
            {
              icon: "save_alt",
              tooltip: "Export to CSV",
              isFreeAction: true,
              onClick: () => exportGlobalToCSV(featureFlags),
            },
          ]}
          editable={{
            onRowUpdate (row) {
              const variables = {
                id: row.id,
                globalEnabled: row.globalEnabled,
                globalDefaultEnabled: row.globalDefaultEnabled,
              };

              return updateFeature({ variables }).catch((err) => {
                logger.sendError(err);
                openSnackbar({ message: "Error updating feature flag", status: "error" });
              });
            },
            onRowDelete (row) {
              return deleteFeature({ variables: { id: row.id } }).catch((err) => {
                logger.sendError(err);
                openSnackbar({ message: "Error deleting feature flag", status: "error" });
              });
            },
          }}
          localization={{ body: { editRow: { deleteText: "Remove feature flag entirely? This will also remove property feature flags." } } }}
        />
      )}

      {tabIndex === TabsIndex.Property && (
        <EditableTable
          title="Property"
          data={propertyFeatureFlags}
          isLoading={loading}
          options={{ pageSize: 25, pageSizeOptions: [5, 10, 20, 25] }}
          columns={propertyColumns}
          actions={[
            {
              icon: "save_alt",
              tooltip: "Export to CSV",
              isFreeAction: true,
              onClick: () => exportPropertyToCSV(propertyFeatureFlags, properties, featureFlags),
            },
          ]}
          editable={{
            onRowAdd (row: PropertyFeatureFlagType) {
              const variables = {
                propertyId: row.propertyId,
                featureId: row.featureId,
                enabled: row.enabled,
              };
              return createPropertyFeature({ variables }).catch((err) => {
                logger.sendError(err);
                openSnackbar({ message: "Error creating property feature flag", status: "error" });
              });
            },
            onRowUpdate (row: PropertyFeatureFlagType) {
              const variables = { id: row.id, enabled: row.enabled };
              return updatePropertyFeature({ variables }).catch((err) => {
                logger.sendError(err);
                openSnackbar({ message: "Error updating property feature flag", status: "error" });
              });
            },
            onRowDelete (row: PropertyFeatureFlagType) {
              return deletePropertyFeature({ variables: { id: row.id } }).catch((err) => {
                logger.sendError(err);
                openSnackbar({ message: "Error deleting property feature flag", status: "error" });
              });
            },
          }}
        />
      )}
    </div>
  );
};

export default FeatureFlagsPage;
